import React from "react";
import { CheckIcon, SparklesIcon } from "@heroicons/react/24/solid";

// Shared pricing/plan card used by both the Pricing page and the post-signup
// ChoosePlan screen. Presentational only — the parent owns selection/CTA state.

const money = (minor, currency) => `${currency} ${(minor / 100).toFixed(0)}`;

const formatPrice = (minor, currency, interval) => {
  if (!minor) return { amount: "Free", suffix: "" };
  return { amount: money(minor, currency), suffix: `/${interval}` };
};

/**
 * Props:
 *  - plan       plan object from /api/plans/
 *  - isCurrent  user is already on this tier
 *  - selected   visually emphasised (used by ChoosePlan)
 *  - busy       CTA shows a saving state
 *  - ctaLabel   button text
 *  - onSelect   (plan) => void
 *  - disabled   disable the CTA
 */
const PlanCard = ({
  plan,
  isCurrent = false,
  selected = false,
  busy = false,
  ctaLabel,
  onSelect,
  disabled = false,
}) => {
  const price = formatPrice(
    plan.price_minor_units,
    plan.currency,
    plan.billing_interval
  );
  const isContactSales = plan.cta_type === "contact_sales";
  const isAdvisor = plan.tier_key === "advisor";
  const compareMinor = plan.compare_at_price_minor_units || 0;
  const hasDiscount = compareMinor > (plan.price_minor_units || 0);

  return (
    <div
      className={`relative rounded-2xl p-6 flex flex-col backdrop-blur-xl border shadow-glass dark:shadow-glass-dark transition-all ${
        selected
          ? "border-indigo-500 ring-2 ring-indigo-500/50 bg-indigo-500/[0.06] dark:bg-indigo-500/[0.10]"
          : plan.highlight
          ? "border-indigo-400 dark:border-indigo-500/60 bg-indigo-500/[0.04] dark:bg-indigo-500/[0.08] ring-1 ring-indigo-400/40"
          : isAdvisor
          ? "border-amber-400/50 dark:border-amber-500/40 bg-amber-500/[0.04] dark:bg-amber-500/[0.06]"
          : "border-white/40 dark:border-white/10 bg-white/60 dark:bg-[#1a2332]/60"
      }`}
    >
      {plan.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-widest">
          <SparklesIcon className="w-3 h-3" /> Most popular
        </span>
      )}

      <div className="flex items-center gap-2">
        <h3 className="text-xl font-bold">{plan.name}</h3>
        {isAdvisor && (
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500">
            Pro track
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 min-h-[2.5rem]">
        {plan.tagline}
      </p>

      <div className="mt-4 mb-5 min-h-[3.25rem]">
        {isContactSales ? (
          <div>
            <span className="text-3xl font-bold">Custom</span>
            <p className="text-xs text-gray-500 mt-1">Let's find the right fit.</p>
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-2 flex-wrap">
              {hasDiscount && (
                <span className="text-lg text-gray-400 line-through">
                  {money(compareMinor, plan.currency)}
                </span>
              )}
              <span className="text-3xl font-bold">{price.amount}</span>
              <span className="text-sm text-gray-500">{price.suffix}</span>
            </div>
            {hasDiscount && (
              <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500">
                🚀 Launch offer · Free for early users
              </span>
            )}
          </>
        )}
      </div>

      <ul className="space-y-2 flex-1">
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <CheckIcon className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
            <span className="text-gray-700 dark:text-gray-300">{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        disabled={disabled || isCurrent || busy}
        onClick={() => onSelect?.(plan)}
        className={`mt-6 w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
          isCurrent
            ? "bg-emerald-500/15 text-emerald-500 cursor-default"
            : plan.highlight
            ? "bg-indigo-600 text-white hover:bg-indigo-500"
            : isAdvisor
            ? "bg-amber-500 text-white hover:bg-amber-400"
            : "border border-gray-300 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-white/5"
        } ${busy ? "opacity-60 cursor-wait" : ""}`}
      >
        {ctaLabel}
      </button>
    </div>
  );
};

export default PlanCard;
